import * as React from 'react'
import * as PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { Fade } from 'react-reveal'
import sanitizeHTML from '../../../services/sanitizeHTML'

import LinkButton from '../../ui/LinkButton/LinkButton'
import LoadingBase from '../../loading/LoadingBase/LoadingBase'

import truncateString from '../../../services/data/truncateString'
import media from '../../../services/generateMediaQueries'

import ZingIconFeatured from '../../../assets/img/zing-icon.webp'

let LOADING = false

const CardWrapper = styled.div`
  outline: none;
`

const CardBannerImage = styled.div.attrs(props => ({
  style: {
    background: `linear-gradient(rgba(141,122,157,0.30), rgba(141,122,157,0.30)), linear-gradient(32deg, rgba(255,255,255,0.00) 69%, #FFFFFF 100%), url('${props.bannerImg}')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
}))`
  display: none;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  transition: all 0.5s;
  min-width: 300px;
  min-height: 150px;

  ${media.lessThan('medium')`
    display: block;
  `}
`

const CardFeaturedImage = styled.div.attrs(props => ({
  style: {
    background: `linear-gradient(rgba(141,122,157,0.30), rgba(141,122,157,0.30)), linear-gradient(32deg, rgba(255,255,255,0.00) 69%, #FFFFFF 100%), url('${props.featuredImg}')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
}))`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  transition: all 0.5s;

  ${media.lessThan('medium')`
    display: none;
  `}
`

const StyledCard = styled.div`
  display: flex;
  flex: 1;
  width: 1000px;
  height: 400px;
  background: #ffffff;
  border-radius: var(--border-radius-card);
  box-shadow: var(--box-shadow-large);
  margin: 0 1rem;
  overflow: hidden;

  ${media.lessThan('medium')`
    position: relative;
    flex-direction: column-reverse;
    width: 300px;
    height: 500px;
    margin: 0;
    margin-right: 1rem;
  `}

  &:hover {
    ${CardBannerImage} {
      transform: scale(1.2);
    }
    ${CardFeaturedImage} {
      transform: scale(1.2);
    }
  }
`

const CardImageWrapper = styled.div`
  position: relative;
  min-width: 400px;
  min-height: 400px;
  overflow: hidden;

  ${() =>
    LOADING
      ? css`
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          background-color: #f1f1f1;
        `
      : css`
          display: grid;
        `}

  ${media.lessThan('medium')`
    min-width: 300px;
    min-height: 180px;
  `}
`

const CardContent = styled.div`
  position: relative;
  height: 100%;
  padding: 2.9375rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${media.lessThan('medium')`
    position: unset;
    padding: 1.5rem;
  `}
`

const Schedule = styled.h3`
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: var(--font-weight-boldest);
  letter-spacing: 0.46px;
  color: #9b9b9b;
  background: #f4f4f4;
  border-radius: var(--border-radius-small);
  max-width: 100%;
  margin: 0;
  margin-bottom: 1.6875rem;
  padding: 8px 16px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  text-align: left;
  text-transform: uppercase;
`

const Title = styled.h1`
  font-size: 1.5rem;
  line-height: 1.9375rem;
  color: var(--color-fg);
  font-weight: var(--font-weight-bold);
  margin-top: 0;
  margin-bottom: 1.5rem;
  font-family: var(--font-family-ayala-display-sans);

  ${media.lessThan('medium')`
    font-size: 1.25rem;
    line-height: 1.5rem;
  `}
`

const Body = styled.div`
  font-size: 1.125rem;
  line-height: 1.4375rem;
  color: var(--color-fg-light);
  font-weight: var(--font-weight);
  margin-bottom: 1.5rem;
  max-height: 100px;
  overflow: hidden;

  > p:first-child {
    margin-top: 0;
  }

  > figure {
    margin: 0;

    > img {
      max-width: 100%;
    }

    > table {
      width: 100%;
      margin: 0 -0.5rem;
      border-collapse: collapse;
      td,
      th {
        padding: 0 0.5rem;
        border: 1px solid #ccc;
      }
    }
  }

  ${media.lessThan('medium')`
    font-size: 0.875rem;
  `}
`

const BottomWrapper = styled.div`
  position: absolute;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.26) 0%,
    rgba(255, 255, 255, 1) 20%,
    rgba(255, 255, 255, 1) 100%
  );
  width: 100%;
  bottom: 0;
  left: 0;
  padding: 2.5rem;

  ${media.lessThan('medium')`
    padding: 1.5rem;
  `}
`

const StyledLinkButton = styled(LinkButton)`
  background: var(--color-primary);
  color: #ffffff !important;

  &:hover {
    background: var(--color-primary-hover);
    color: #ffffff !important;
  }
`

const ZingIcon = styled.img.attrs({ src: ZingIconFeatured })`
  margin-bottom: 1rem;
  margin: auto;
  width: 7rem;
`

const Text = styled.p`
  margin: 0;
`

const BannerTag = styled.div`
  position: absolute;
  z-index: 5;
  right: 0;
  margin: 2rem;
  padding: 0.5rem;
  font-weight: var(--font-weight-boldest);
  background-color: #e7475e;
  min-width: 5rem;
  text-align: center;
  border-radius: var(--border-radius-small);
  font-size: 12px;
  color: white;
`

function FeaturedCard(props) {
  const {
    featuredImgUrl = '',
    bannerImgUrl = '',
    schedule = '',
    title = '',
    body = '',
    link = '',
    id = 0,
    onClick,
    onRetry,
    loading = false,
    error = false,
    label,
  } = props

  const truncatedTitle = truncateString(title, 56)
  const truncatedBody = truncateString(body, 115)
  LOADING = loading

  const CardComponent = () => (
    <CardWrapper>
      <StyledCard>
        {!error && (
          <CardContent>
            {!LOADING ? (
              <>
                {!!schedule && <Schedule>{schedule}</Schedule>}
                <Title>{truncatedTitle}</Title>
                <Body
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(truncatedBody),
                  }}
                />
              </>
            ) : (
              <LoadingBase lines={3} />
            )}
            <BottomWrapper>
              <StyledLinkButton small to={link} onClick={onClick}>
                Read more
              </StyledLinkButton>
            </BottomWrapper>
          </CardContent>
        )}
        <CardImageWrapper
          style={
            error && {
              width: '100%',
              height: '100%',
              cursor: 'pointer',
            }
          }
          onClick={error && onRetry}
        >
          {!LOADING ? (
            <>
              {label && <BannerTag>{label}</BannerTag>}
              <CardBannerImage bannerImg={bannerImgUrl} />
              <CardFeaturedImage featuredImg={featuredImgUrl} />
            </>
          ) : (
            <>
              <ZingIcon />
              {error && (
                <>
                  <Text>Failed to load Top Picks.</Text>
                  <Text>Tap to retry.</Text>
                </>
              )}
            </>
          )}
        </CardImageWrapper>
      </StyledCard>
    </CardWrapper>
  )

  if (LOADING) {
    return <CardComponent />
  }

  return (
    <Fade right distance="1rem" delay={300 + 50 * id} duration={300} force>
      <CardComponent />
    </Fade>
  )
}

FeaturedCard.defaultProps = {
  link: '',
  schedule: '',
  error: null,
}

FeaturedCard.propTypes = {
  featuredImgUrl: PropTypes.string,
  bannerImgUrl: PropTypes.string,
  schedule: PropTypes.string,
  title: PropTypes.string,
  body: PropTypes.string,
  link: PropTypes.string,
  onClick: PropTypes.func,
  onRetry: PropTypes.func,
  loading: PropTypes.bool,
  error: PropTypes.bool,
}

export default FeaturedCard
